/**
 * 网络请求工具函数
 * Created by PeakXin on 2025/10/29
 * Optimized on 2025/11/03
 */
import axios from 'axios'
import { Toast } from 'vant'
import store from '@/store'
import router from '@/router'
import { redirectToWechatAuth } from '@/utils/wxauth'

// 创建 axios 实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 10000 // 请求超时时间，略微放宽
})

// ========== Request 拦截 ==========
service.interceptors.request.use(
  (config) => {
    // 在发送请求之前做某件事
    if (store.getters.token) {
      // 让每个请求携带token
      config.headers['Token'] = store.getters.token
    }
    return config
  },
  (error) => {
    // 请求错误处理
    console.error('Request Error:', error)
    return Promise.reject(error)
  }
)

// ========== Response 拦截 ==========
service.interceptors.response.use(
  /**
   * If you want to get http information such as headers or status
   * Please return  response => response
   */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code
   */
  (response) => {
    if (response.status === 200) {
      const res = response.data

      // 根据返回的code值来做不同的处理
      if (res.errCode !== 0) {
        Toast.fail(res.errMsg || '请求失败')

        // 登录状态问题处理,10100: 未登录 10102: Token过期
        if ([10100, 10101, 10102, 10103].includes(res.errCode)) {
          Toast.fail('登录状态已过期，请重新登录')
          store.dispatch('user/resetToken')

          // 仅在非白名单页面才触发授权跳转
          // 也可以这样获取store.state.app.isWhiteListed
          if (!store.getters['app/isWhiteListed']) {
            redirectToWechatAuth()
          } else {
            console.log('[Auth] 当前为白名单页面，不触发授权流程')
          }
        }
      }
      return res
    } else {
      return Promise.reject(new Error(res.errMsg || 'Error'))
    }
  },
  (error) => {
    console.error('Response Error:', error)

    let message = error.message
    if (error.response) {
      switch (error.response.status) {
        case 401:
          message = '未授权，请重新登录'
          store.dispatch('user/resetToken').then(() => {
            router.push('/login')
          })
          break
        case 403:
          message = '拒绝访问'
          break
        case 404:
          message = '请求错误，未找到该资源'
          break
        case 500:
          message = '服务器端出错'
          break
        default:
          message = '网络连接故障'
      }
    }

    Toast.fail(message)
    return Promise.reject(error)
  }
)

// ========== 常用请求封装 ==========

/**
 * GET 请求封装
 * @param {string} url 请求地址
 * @param {object} params 请求参数
 * @param {object} config 其他 axios 配置（可选）
 */
export const get = (url, params = {}, config = {}) => {
  return service({
    url,
    method: 'get',
    params,
    ...config
  })
}

/**
 * POST 请求封装
 * @param {string} url 请求地址
 * @param {object} data 请求体
 * @param {object} config 其他 axios 配置（可选）
 */
export const post = (url, data = {}, config = {}) => {
  return service({
    url,
    method: 'post',
    data,
    ...config
  })
}

/**
 * 文件上传请求封装
 * @param {string} url 上传地址
 * @param {object} fileData 文件数据（FormData）
 * @param {object} config 其他配置（可选）
 */
export const upload = (url, fileData, config = {}) => {
  return service({
    url,
    method: 'post',
    data: fileData,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(config.headers || {})
    },
    ...config
  })
}

/**
 * 批量文件上传
 * @param {string} url 上传接口地址
 * @param {File[] | FileList} files 文件数组或 FileList
 * @param {object} extraParams 附加参数，例如 { userId: 123, type: 'avatar' }
 * @param {object} config axios 额外配置（可选）
 */
export const uploadMultiple = (url, files, extraParams = {}, config = {}) => {
  const formData = new FormData()

  // 附加多个文件
  Array.from(files).forEach((file) => {
    formData.append('files', file)
  })

  // 附加其他参数
  Object.keys(extraParams).forEach((key) => {
    formData.append(key, extraParams[key])
  })

  return service({
    url,
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...(config.headers || {})
    },
    ...config
  })
}

export default service
